import React, { useState, useRef, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Button, Modal } from "common/components";
import {
  ArrowBack,
  Add,
  Save,
  Storage,
  Transform,
  Adjust,
  Close
} from "@mui/icons-material";
import HeaderNew from "common/components/custom/header-new/HeaderNew";
import HubHeader from "common/components/custom/hub-header/HubHeader";
import { MainRoutes } from "common/config/route.config";
import { Connection, DataFlow, FlowNode, mockConnections } from "../../data";
import { dataSources } from "../../data/models/data-sources.model";
import { FlowStorage } from "../../data/flow-storage";
import "./FlowBuilder.scss";

const FlowBuilder: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [flowName, setFlowName] = useState("");
  const [flowDescription, setFlowDescription] = useState("");
  const [nodes, setNodes] = useState<FlowNode[]>([]);
  const [selectedNode, setSelectedNode] = useState<FlowNode | null>(null);
  const [showNodeModal, setShowNodeModal] = useState(false);
  const [nodeType, setNodeType] = useState<
    "source" | "transform" | "destination"
  >("source");
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingFlowId, setEditingFlowId] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);

  // Load existing flow data if in edit mode
  useEffect(() => {
    const editFlowId = searchParams.get("edit");
    if (editFlowId) {
      const existingFlow = FlowStorage.getFlow(editFlowId);
      if (existingFlow) {
        setIsEditMode(true);
        setEditingFlowId(editFlowId);
        setFlowName(existingFlow.name);
        setFlowDescription(existingFlow.description || "");
        setNodes(existingFlow.nodes || []);
      } else {
        console.error("Flow not found for editing:", editFlowId);
        // Redirect back to data integration if flow not found
        navigate(MainRoutes.DATA_INTEGRATION_HUB + "?tab=flows");
      }
    }
  }, [searchParams, navigate]);

  const handleAddNode = (type: "source" | "transform" | "destination") => {
    setNodeType(type);
    setShowNodeModal(true);
  };

  const handleCreateNode = (label: string, connectionId?: string) => {
    const newNode: FlowNode = {
      id: Date.now().toString(),
      type: nodeType,
      label,
      connectionId,
      position: { x: 100 + nodes.length * 50, y: 100 + nodes.length * 30 }
    };
    setNodes([...nodes, newNode]);
    setShowNodeModal(false);
  };

  const handleSaveFlow = () => {
    if (!flowName.trim()) return;

    const flow: DataFlow = {
      id: isEditMode && editingFlowId ? editingFlowId : Date.now().toString(),
      name: flowName,
      description: flowDescription || "Data integration flow",
      nodes,
      connections: [],
      status: "draft",
      createdAt:
        isEditMode && editingFlowId
          ? FlowStorage.getFlow(editingFlowId)?.createdAt ||
            new Date().toISOString()
          : new Date().toISOString()
    };

    if (isEditMode && editingFlowId) {
      // Update existing flow
      FlowStorage.updateFlow(flow);
    } else {
      // Save new flow
      FlowStorage.addFlow(flow);
    }

    // Navigate back to integration hub with flows tab active
    navigate(MainRoutes.DATA_INTEGRATION_HUB + "?tab=flows");
  };
  const handleBack = () => {
    navigate(MainRoutes.DATA_INTEGRATION_HUB);
  };

  const getNodeIcon = (type: FlowNode["type"]) => {
    switch (type) {
      case "source":
        return <Storage className="node-icon" />;
      case "transform":
        return <Transform className="node-icon" />;
      case "destination":
        return <Adjust className="node-icon" />;
    }
  };

  const getNodeClass = (type: FlowNode["type"]) => {
    switch (type) {
      case "source":
        return "node-source";
      case "transform":
        return "node-transform";
      case "destination":
        return "node-destination";
    }
  };

  const closeNodeModal = () => {
    setShowNodeModal(false);
  };

  return (
    <>
      <HeaderNew showSearch={false} />

      <div className="flow-builder">
        <div className="flow-builder-content">
          <HubHeader
            title="Data Integration Hub"
            description="Centralized platform for seamless data integration and workflow management."
            showBackButton={true}
            backRoute={MainRoutes.DATA_INTEGRATION_HUB}
          />

          <div className="section-header">
            <div className="section-info">
              <h2>{isEditMode ? "Edit Flow" : "Flow Builder"}</h2>
              <p>
                {isEditMode
                  ? "Update your data integration flow"
                  : "Design your data integration flow with visual components"}
              </p>
            </div>
          </div>

          <div className="flow-builder-main">
            {/* Left Panel - Configuration */}
            <div className="flow-config-panel">
              <div className="config-section">
                <h3>Flow Details</h3>
                <div className="form-group">
                  <label htmlFor="flow-name">Flow Name</label>
                  <input
                    id="flow-name"
                    className="form-input"
                    placeholder="My Data Flow"
                    value={flowName}
                    onChange={(e) => setFlowName(e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="flow-description">Description</label>
                  <textarea
                    id="flow-description"
                    className="form-textarea"
                    placeholder="Describe what this flow does..."
                    value={flowDescription}
                    onChange={(e) => setFlowDescription(e.target.value)}
                    rows={3}
                  />
                </div>
              </div>

              <div className="config-section">
                <h3>Add Components</h3>
                <div className="component-buttons">
                  <Button
                    variant="secondary"
                    onClick={() => handleAddNode("source")}
                    className="component-button"
                  >
                    <Storage />
                    Add Source
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => handleAddNode("transform")}
                    className="component-button"
                  >
                    <Transform />
                    Add Transform
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => handleAddNode("destination")}
                    className="component-button"
                  >
                    <Adjust />
                    Add Destination
                  </Button>
                </div>
              </div>

              <div className="config-section">
                <h3>Components</h3>
                <div className="components-count">
                  {nodes.length} component{nodes.length !== 1 ? "s" : ""} added
                </div>
                <div className="components-list">
                  {nodes.map((node) => (
                    <div
                      key={node.id}
                      className={`component-item ${
                        selectedNode?.id === node.id ? "selected" : ""
                      }`}
                      onClick={() => setSelectedNode(node)}
                    >
                      <div className="component-info">
                        {getNodeIcon(node.type)}
                        <div className="component-details">
                          <div className="component-name">{node.label}</div>
                          <div className="component-type">{node.type}</div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Panel - Canvas */}
            <div className="flow-canvas-panel">
              <div className="canvas-header">
                <h3>Flow Canvas</h3>
                <p>Drag and arrange your components</p>
              </div>
              <div ref={canvasRef} className="flow-canvas">
                {nodes.length === 0 ? (
                  <div className="empty-canvas">
                    <Add className="empty-icon" />
                    <p>Add components to start building your flow</p>
                  </div>
                ) : (
                  nodes.map((node, index) => (
                    <div
                      key={node.id}
                      className={`flow-node ${getNodeClass(node.type)}`}
                      style={{
                        left: node.position.x,
                        top: node.position.y
                      }}
                    >
                      <div className="node-content">
                        {getNodeIcon(node.type)}
                        <div className="node-details">
                          <div className="node-name">{node.label}</div>
                          <div className="node-type">{node.type}</div>
                        </div>
                      </div>
                      {index < nodes.length - 1 && (
                        <div className="node-connector" />
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          {/* Save button positioned at bottom right */}
          <div className="save-flow-section">
            <div className="action-buttons">
              <Button
                variant="primary"
                onClick={handleSaveFlow}
                disabled={!flowName.trim()}
              >
                <Save />
                {isEditMode ? "Update Flow" : "Save Flow"}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <AddNodeModal
        show={showNodeModal}
        onClose={closeNodeModal}
        nodeType={nodeType}
        connections={mockConnections}
        onCreate={handleCreateNode}
      />
    </>
  );
};

interface AddNodeModalProps {
  show: boolean;
  onClose: () => void;
  nodeType: "source" | "transform" | "destination";
  connections: Connection[];
  onCreate: (label: string, connectionId?: string) => void;
}

const AddNodeModal: React.FC<AddNodeModalProps> = ({
  show,
  onClose,
  nodeType,
  connections,
  onCreate
}) => {
  const [label, setLabel] = useState("");
  const [selectedConnection, setSelectedConnection] = useState("");

  const handleCreate = () => {
    if (!label.trim()) return;
    onCreate(label, selectedConnection || undefined);
    setLabel("");
    setSelectedConnection("");
  };

  const needsConnection = nodeType === "source" || nodeType === "destination";

  const modalFooter = (
    <div className="modal-footer">
      <Button variant="secondary" onClick={onClose}>
        Cancel
      </Button>
      <Button variant="primary" onClick={handleCreate} disabled={!label.trim()}>
        Add Component
      </Button>
    </div>
  );

  return (
    <Modal
      title={`Add ${
        nodeType.charAt(0).toUpperCase() + nodeType.slice(1)
      } Component`}
      show={show}
      onClose={onClose}
      size="md"
      footerElement={modalFooter}
    >
      <div className="add-node-modal-content">
        <p className="modal-description">
          Configure the {nodeType} component for your data flow
        </p>

        <div className="form-group">
          <label htmlFor="node-label">Component Name</label>
          <input
            id="node-label"
            className="form-input"
            placeholder={`My ${nodeType}`}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            autoFocus
          />
        </div>

        {needsConnection && (
          <div className="form-group">
            <label htmlFor="connection">Connection</label>
            <select
              id="connection"
              className="form-select"
              value={selectedConnection}
              onChange={(e) => setSelectedConnection(e.target.value)}
            >
              <option value="">Select a connection</option>
              {connections.map((conn) => {
                const sourceInfo = dataSources.find(
                  (ds: any) => ds.type === conn.dataSourceType
                );
                return (
                  <option key={conn.id} value={conn.id}>
                    {sourceInfo?.icon} {conn.name}
                  </option>
                );
              })}
            </select>
          </div>
        )}
      </div>
    </Modal>
  );
};

export default FlowBuilder;
